import { jsx as _jsx } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useMemo } from 'react';
import { useMatch } from 'react-router-dom';
import { getApiInstance } from '../../helpers/api.helpers';
import { getUserLocaleLocalStorageKey } from '../../helpers/local_storage.helpers';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
import { AxonAccountContext } from '../axon_account';
import { SiteContext } from '../site';
export const OrgContext = createContext({
    isLoading: true,
    org: null,
    participantStatusMap: new Map(),
});
export const OrgProvider = ({ children }) => {
    const match = useMatch('/site/study/:studyId/*');
    const { account } = useContext(AxonAccountContext);
    const { site } = useContext(SiteContext);
    const { environment } = useCurrentEnvironment(match?.params?.studyId);
    const getOrg = async () => {
        if (!environment || !account || !site) {
            return;
        }
        const url = `/v2/account/${account._id}/c_sites/${site?._id}/c_study`;
        const userLocaleKey = getUserLocaleLocalStorageKey({
            environmentId: environment.id,
            axonUserId: account._id,
        });
        const userLocale = localStorage.getItem(userLocaleKey) || undefined;
        const { data: response } = await getApiInstance(environment, userLocale).get(url, {
            params: {
                expand: ['c_study', 'dcr__default_dcr_configurations.dcr__dcr_configuration', 'c_locks'],
                include: ['c_subject_status_list'],
            },
        });
        return response || null;
    };
    const { data: org, isLoading } = useQuery({
        queryKey: ['getOrg', site?._id],
        queryFn: getOrg,
        enabled: !!environment && !!account && !!site,
    });
    // the c_status_label is the localized representation of the c_public_user.c_status
    const participantStatusMap = useMemo(() => {
        const map = new Map();
        org?.c_subject_status_list?.forEach((status) => {
            map.set(status.c_status_value, status.c_status_label);
        });
        return map;
    }, [org?.c_subject_status_list]);
    const value = useMemo(() => ({
        isLoading,
        org,
        participantStatusMap,
    }), [org, participantStatusMap]);
    return _jsx(OrgContext.Provider, { value: value, children: children });
};
